import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import Toggle from 'react-toggle';
import { FaUndo, FaFilter } from 'react-icons/fa';
import { Dropdown } from 'react-bootstrap';
import Axios from 'axios';
import TextSearch from '@/shared/components/input/TextSearch';
import intlShape from '@/shared/utils/intlShape';
import RelationTypeButtonGroup from '@/shared/components/RelationTypeButtonGroup';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import RelationListPicker from '@/shared/components/input/RelationListPicker';
import YesNoSingleSelect from '@/shared/components/input/select/YesNoSingleSelect';
import MultiSelect, {
  removeItem as removeMultiSelectItem,
  addItem as addMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import api from '@/shared/api';
import guid from '@/shared/utils/guid';
import momentPropTypes from 'react-moment-proptypes';
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import enumCache from '@/shared/services/EnumCacheService';
import userStorage from '@/shared/services/userStorage';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '../../../shared/components/DynamicFilter';
import messages from './relationOverviewMessages';
import config from '../../../config';
import moment from 'moment';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { FilterSave } from '@/shared/components/FilterSave';

export const defaultRelationFilters = {
  assistant: null, // UserId
  responsible: null, // UserId
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  assignedTo: null, // UserId
  searchValue: '', // String,
  communicationChannelSearchValue: '', // String
  customerCodeSearchValue: '', // String
  includeInactive: null,
  type: [], // Array
  crmTypes: [], // Array
  legalForms: [], // Array
  withLabels: [], // Array
  withoutLabels: [], // Array
  linkType: null,
  linkTypeReverse: null,
  vatPeriodicities: [],
  paymentmethods: [],
  endFiscalYears: [],
  ownCompanies: [],
  legalEntities: [],
  languageCode: '',
  countryCode: '',
  teams: [],
  withoutTeams: [],
  financialStatementSchemes: [],
  shareRegisters: [],
  jurisdictions: [],
  sectors: [],
  socialSecretariats: [],
  socialSecurityFunds: [],
  accountingSoftware: [],
  receiveInvoiceSoftware: [],
  otherSoftware: [],
  sendInvoiceSoftware: [],
  sendInvoiceStrategy: [],
  peppolStatus: [],
  mixedTaxPayer: [],
  sendInvoicePeppolReady: [],
  mixedTaxPayerVatDeductionMethod: [],
  naceCodes: [],
  filterBehaviours: {
    withLabels: 1,
    naceCodes: 1,
  },
  relationGroups: [],
  startDateFrom: null,
  startDateUntil: null,
  withAssignments: [],
  pep: null,
  linkedToPep: null,
  nationalityCode: '',
  totalRisk: [],
  selfBooker: [],
  relationHandlingLevels: [],
  vatNumberTypes: [],
  relations: [],
  isPeppolRequired: null,
};

const dynamicFiltersStorageKey = 'RelationOverviewDynamicFilters';

class RelationOverviewFilters extends Component {
  state = {
    linkTypes: [],
    linkTypesReverse: [],
    teams: [],
    assignmentTemplates: [],
    languages: [],
    countries: [],
    vatPeriodicities: [],
    paymentmethods: [],
    dynamicFilters: [],
    selectedRelations: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      assignedTo: PropTypes.string,
      searchValue: PropTypes.string,
      communicationChannelSearchValue: PropTypes.string,
      customerCodeSearchValue: PropTypes.string,
      includeInactive: PropTypes.number,
      type: PropTypes.arrayOf(PropTypes.number),
      crmTypes: PropTypes.arrayOf(PropTypes.number),
      legalForms: PropTypes.arrayOf(PropTypes.number),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      linkType: PropTypes.number,
      linkTypeReverse: PropTypes.number,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      teams: PropTypes.arrayOf(PropTypes.string),
      withoutTeams: PropTypes.arrayOf(PropTypes.string),
      sectors: PropTypes.arrayOf(PropTypes.number),
      accountingSoftware: PropTypes.arrayOf(PropTypes.number),
      receiveInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      otherSoftware: PropTypes.arrayOf(PropTypes.number),
      sendInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      sendInvoiceStrategy: PropTypes.arrayOf(PropTypes.number),
      peppolStatus: PropTypes.arrayOf(PropTypes.number),
      naceCodes: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      startDateFrom: PropTypes.oneOfType([PropTypes.string, momentPropTypes.momentObj]),
      startDateUntil: PropTypes.oneOfType([PropTypes.string, momentPropTypes.momentObj]),
      totalRisk: PropTypes.arrayOf(PropTypes.number),
      relationHandlingLevels: PropTypes.arrayOf(PropTypes.number),
      vatNumberTypes: PropTypes.arrayOf(PropTypes.number),
      relationPicker: PropTypes.arrayOf(PropTypes.string),
      isPeppolRequired: PropTypes.bool,
      pep: PropTypes.bool,
      linkedToPep: PropTypes.bool,
    }),
    intl: intlShape.isRequired,
    countSelectedRelations: PropTypes.string.isRequired,
  };

  static defaultProps = {
    filters: defaultRelationFilters,
  };

  componentDidMount() {
    const { intl, filters } = this.props;

    const relationsPromise =
      filters.relations?.length > 0
        ? api.base.post(`${config.appUrls.api}/api/relation/names`, filters.relations)
        : () => Promise.resolve();

    Promise.all([
      enumCache.get('linktype'),
      enumCache.get('paymentmethod'),
      this.getTeams(),
      enumCache.get('shareregister'),
      enumCache.get('vatNumberType'),
      enumCache.get('inactiveRelationFilter'),
      this.getAssignmentTemplates(),
      relationsPromise,
    ]).then(
      ([
        linktypes,
        paymentmethods,
        teams,
        shareRegister,
        vatNumberTypes,
        includeInactiveOptions,
        assignmentTemplates,
        { data: relationNames },
      ]) => {
        const dataCache = DataCacheService.getDataCache();
        const allDynamicFilters = [
          ...getDynamicFiltersFromDefaultFilters(defaultRelationFilters, 'crmTypes'),

          { id: 'customerCodeSearch', label: messages.CustomerCodeSearch },
          { id: 'linkType', label: messages.LinkType },
          { id: 'linkTypeReverse', label: messages.LinkTypeReverse },
          { id: 'includeInactive', label: messages.FilterInactiveRelations },
          { id: 'languageCode', label: messages.Language },
          { id: 'countryCode', label: messages.Country },
          { id: 'teams', label: messages.WithTeams },
          { id: 'withoutTeams', label: messages.WithoutTeams },
          { id: 'withAssignments', label: messages.WithAssignments },
          { id: 'shareRegisters', label: messages.ShareRegister },
          { id: 'jurisdiction', label: messages.Jurisdiction },
          { id: 'paymentMethod', label: messages.PaymentMethod },
          {
            id: 'naceCode',
            label: messages.NaceCode,
            enabled: DataCacheService.isBelgianTenantConfiguration(),
          },
          { id: 'startDate', label: messages.StartDate },
          { id: 'nationalityCode', label: messages.Nationality },
          { id: 'vatNumberTypes', label: messages.VatNumberType },
          { id: 'relationPicker', label: messages.RelationPicker },
          { id: 'isPeppolRequired', label: messages.PeppolRequired },
        ];
        const specificCountryCodes = ['BE', 'NL', 'FR', 'LU', 'GB'];

        // Create an array for the specific country codes in the format you want
        const specificCountryOptions = specificCountryCodes
          .map(code => {
            const country = dataCache.country.find(x => x.code === code);
            return {
              id: country ? country.code : code,
              label: intl.formatMessage(listItemMsg('country', code, country?.description || '')),
            };
          })
          .sort(dynamicSort('label'));

        // Create an array for "All other countries" option
        const otherCountryOptions = dataCache.country
          .filter(x => !specificCountryCodes.includes(x.code))
          .map(x => ({
            id: x.code,
            label: intl.formatMessage(listItemMsg('country', x.code, x.description || '')),
          }))
          .sort(dynamicSort('label')); // Sort the array based on the translated labels

        // Combine the arrays of specific country codes and all other countries
        const combinedOptions = [...specificCountryOptions, ...otherCountryOptions];

        let shareRegisters = [
          { id: -1, label: intl.formatMessage(messages.ShareRegisterNotFilledOut) },
          ...shareRegister.map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('shareRegister', x.key, x.value)),
          })),
        ];
        this.setState({
          naceCodes: dataCache.naceCodes.map(x => ({
            id: x.Code,
            label: `${x.Code} - ${x.Name}`,
          })),
          linkTypes: linktypes
            .map(x => ({
              id: x.key,
              label: intl.formatMessage(listItemMsg('linktypeto', x.key, x.value)),
            }))
            .sort(dynamicSort('label')),
          linkTypesReverse: linktypes
            .map(x => ({
              id: x.key,
              label: intl.formatMessage(listItemMsg('linktypeback', x.key, x.value)),
            }))
            .sort(dynamicSort('label')),
          shareRegisters,
          vatNumberTypes: vatNumberTypes.map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('vatNumberType', x.key, x.value)),
          })),
          includeInactiveOptions: includeInactiveOptions.map(x => ({
            id: x.key,
            label: intl.formatMessage(listItemMsg('inactiveRelationFilter', x.key, x.value)),
          })),
          paymentmethods: [
            ...paymentmethods
              .map(x => ({
                id: x.key,
                label: intl.formatMessage(listItemMsg('paymentmethod', x.key, x.value)),
              }))
              .sort(dynamicSort('label')),
          ],
          teams,
          assignmentTemplates,
          languages: dataCache.language.map(x => ({
            id: x.code,
            label: intl.formatMessage(listItemMsg('language', x.code, x.description || '')),
          })),
          countries: combinedOptions,
          dynamicFilters: initDynamicFilters(
            intl,
            allDynamicFilters,
            userStorage.getItem(dynamicFiltersStorageKey) || [],
            filters,
            defaultRelationFilters,
          ),
          jurisdictions: dataCache.jurisdiction
            .map(x => ({
              id: x.id,
              label: intl.formatMessage(listItemMsg('jurisdiction', x.id, x.name || '')),
            }))
            .sort(dynamicSort('label')),
          selectedRelations: relationNames ?? [],
        });
      },
    );
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };

  getTeams = () => {
    const { intl } = this.props;
    this.teamsToken = Axios.CancelToken.source();
    return api.base
      .get(`${config.appUrls.api}/api/team/MyTeams`, {
        cancelToken: this.teamsToken.token,
      })
      .then(({ data }) => [
        { id: guid.empty, label: intl.formatMessage(messages.WithoutTeams) },
        ...data.map(({ id, name }) => ({ id, label: name })).sort(dynamicSort('label')),
      ])
      .catch(error => {
        // Only handle non-cancellation errors
        if (!Axios.isCancel(error)) {
          console.error('Error loading teams:', error);
          return [];
        }
        return [];
      });
  };

  getAssignmentTemplates = () => {
    const { intl } = this.props;
    if (!hasAssignments()) return [];
    this.assignmentTemplatesCancelToken = Axios.CancelToken.source();
    return api.base
      .get(`${config.appUrls.api}/api/AssignmentTemplate/Get`, {
        cancelToken: this.assignmentTemplatesCancelToken.token,
      })
      .then(({ data }) => {
        const { intl } = this.props;
        const templates = data
          .map(x => ({
            ...x,
            code: x.code || '',
            label: `${x.code ? `${x.code} – ` : ''}${intl.formatMessage(
              listItemMsg('assignmenttemplate', x.id, x.name),
            )}`,
          }))
          .sort(dynamicSortMultiple('code', 'label'));

        return templates;
      })
      .catch(error => {
        // Only handle non-cancellation errors
        if (!Axios.isCancel(error)) {
          console.error('Error loading assignment templates:', error);
          return [];
        }
        return [];
      });
  };
  componentWillUnmount() {
    if (this.teamsToken && !this.teamsToken.token.reason) {
      this.teamsToken.cancel('Operation canceled due to leaving page.');
    }
    if (this.assignmentTemplatesCancelToken && !this.assignmentTemplatesCancelToken.token.reason) {
      this.assignmentTemplatesCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }
  resetFilters = () => {
    const { onFilterChange } = this.props;
    this.setState({ selectedRelations: [] });
    onFilterChange({ ...defaultRelationFilters });
  };

  setFilters = filters => {
    this.props.onFilterChange(filters);
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    const startDateFrom =
      typeof filters.startDateFrom === 'string'
        ? moment.utc(filters.startDateFrom)
        : filters.startDateFrom;
    const startDateUntil =
      typeof filters.startDateUntil === 'string'
        ? moment.utc(filters.startDateUntil)
        : filters.startDateUntil;

    switch (type) {
      case 'customerCodeSearch':
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <TextSearch
                value={filters.customerCodeSearchValue}
                className="form-control"
                placeholder={intl.formatMessage(messages.CustomerCodeSearch)}
                onChange={value => this.filterChanged({ customerCodeSearchValue: value })}
              />
            </div>
          </div>
        );
      case 'linkType': {
        const { linkTypes } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ linkType: e.target.value ? parseInt(e.target.value) : null })
                }
                value={filters.linkType || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLinkType)}</option>
                {linkTypes.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'linkTypeReverse': {
        const { linkTypesReverse } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({
                    linkTypeReverse: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                value={filters.linkTypeReverse || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLinkTypeReverse)}</option>
                {linkTypesReverse.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'paymentMethod': {
        const { paymentmethods } = this.state;

        const [selectedPaymentMethods, availablePaymentMethods] = splitMultiSelectItems(
          paymentmethods,
          filters.paymentmethods,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedPaymentMethods}
                onItemRemoved={item =>
                  this.filterChanged({
                    paymentmethods: removeMultiSelectItem(filters.paymentmethods, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    paymentmethods: addMultiSelectItem(filters.paymentmethods, item),
                  })
                }
                items={availablePaymentMethods}
                placeholder={intl.formatMessage(messages.PaymentMethod)}
              />
            </div>
          </div>
        );
      }
      case 'includeInactive': {
        const { includeInactiveOptions } = this.state;

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({
                    includeInactive: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                value={filters.includeInactive ?? ''}
              >
                <option value="">{intl.formatMessage(messages.FilterInactiveRelations)}</option>
                {includeInactiveOptions.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'languageCode': {
        const { languages } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ languageCode: e.target.value ? e.target.value : null })
                }
                value={filters.languageCode || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterLanguage)}</option>
                {languages.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'countryCode': {
        const { countries } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ countryCode: e.target.value ? e.target.value : null })
                }
                value={filters.countryCode || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterCountry)}</option>
                {countries.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      case 'teams': {
        const { teams } = this.state;
        const [selectedTeams, availableTeams] = splitMultiSelectItems(teams, filters.teams);

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedTeams}
                onItemRemoved={item =>
                  this.filterChanged({
                    teams: removeMultiSelectItem(filters.teams, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    teams: addMultiSelectItem(filters.teams, item),
                  })
                }
                items={availableTeams}
                placeholder={intl.formatMessage(messages.WithTeams)}
              />
            </div>
          </div>
        );
      }
      case 'withoutTeams': {
        const { teams } = this.state;
        const noWithoutTeam = teams.filter(x => x.id != guid.empty);
        const [selectedTeams, availableTeams] = splitMultiSelectItems(
          noWithoutTeam,
          filters.withoutTeams,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedTeams}
                onItemRemoved={item =>
                  this.filterChanged({
                    withoutTeams: removeMultiSelectItem(filters.withoutTeams, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    withoutTeams: addMultiSelectItem(filters.withoutTeams, item),
                  })
                }
                items={availableTeams}
                placeholder={intl.formatMessage(messages.WithoutTeams)}
              />
            </div>
          </div>
        );
      }
      case 'withAssignments': {
        const { assignmentTemplates } = this.state;
        const [selectedAssignments, availableAssignments] = splitMultiSelectItems(
          assignmentTemplates,
          filters.withAssignments,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedAssignments}
                onItemRemoved={item =>
                  this.filterChanged({
                    withAssignments: removeMultiSelectItem(filters.withAssignments, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    withAssignments: addMultiSelectItem(filters.withAssignments, item),
                  })
                }
                items={availableAssignments}
                placeholder={intl.formatMessage(messages.WithAssignments)}
              />
            </div>
          </div>
        );
      }
      case 'shareRegisters': {
        const { shareRegisters } = this.state;
        const [selectedShareRegisters, availableShareRegisters] = splitMultiSelectItems(
          shareRegisters,
          filters.shareRegisters,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedShareRegisters}
                onItemRemoved={item =>
                  this.filterChanged({
                    shareRegisters: removeMultiSelectItem(filters.shareRegisters, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    shareRegisters: addMultiSelectItem(filters.shareRegisters, item),
                  })
                }
                items={availableShareRegisters}
                placeholder={intl.formatMessage(messages.ShareRegister)}
              />
            </div>
          </div>
        );
      }
      case 'isPeppolRequired':
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <YesNoSingleSelect
                value={filters[type]}
                onChange={x => this.filterChanged({ [type]: x })}
                placeholder={intl.formatMessage(messages.PeppolRequired)}
              />
            </div>
          </div>
        );
      case 'relationPicker': {
        const { selectedRelations } = this.state;

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <RelationListPicker
                placeholder={intl.formatMessage(messages.ChooseRelations)}
                relations={selectedRelations || []} // has to be a relation object
                onAddRelation={relation => {
                  this.setState({ selectedRelations: [...selectedRelations, relation] }, () => {
                    this.filterChanged({
                      relations: [...filters.relations, relation.id],
                    });
                  });
                }}
                onRemoveRelation={relation => {
                  this.setState(
                    { selectedRelations: selectedRelations.filter(x => x.id !== relation.id) },
                    () => {
                      this.filterChanged({
                        relations: filters.relations.filter(x => x !== relation.id),
                      });
                    },
                  );
                }}
              />
            </div>
          </div>
        );
      }
      case 'vatNumberTypes': {
        const { vatNumberTypes } = this.state;
        const [selectedVatNumberTypes, availableVatNumberTypes] = splitMultiSelectItems(
          vatNumberTypes,
          filters.vatNumberTypes,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedVatNumberTypes}
                onItemRemoved={item =>
                  this.filterChanged({
                    vatNumberTypes: removeMultiSelectItem(filters.vatNumberTypes, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    vatNumberTypes: addMultiSelectItem(filters.vatNumberTypes, item),
                  })
                }
                items={availableVatNumberTypes}
                placeholder={intl.formatMessage(messages.VatNumberType)}
              />
            </div>
          </div>
        );
      }
      case 'jurisdiction': {
        const { jurisdictions } = this.state;
        const [selectedJurisdictions, availableJurisdictions] = splitMultiSelectItems(
          jurisdictions,
          filters.jurisdictions,
        );
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedJurisdictions}
                onItemRemoved={item =>
                  this.filterChanged({
                    jurisdictions: removeMultiSelectItem(filters.jurisdictions, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    jurisdictions: addMultiSelectItem(filters.jurisdictions, item),
                  })
                }
                items={availableJurisdictions}
                placeholder={intl.formatMessage(messages.Jurisdiction)}
              />
            </div>
          </div>
        );
      }
      case 'naceCode': {
        const { naceCodes } = this.state;

        const [selectedNaceCodes, availableNaceCodes] = splitMultiSelectItems(
          naceCodes,
          filters.naceCodes,
        );

        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedNaceCodes}
                onItemRemoved={item =>
                  this.filterChanged({
                    naceCodes: removeMultiSelectItem(filters.naceCodes, item),
                  })
                }
                onItemSelected={item =>
                  this.filterChanged({
                    naceCodes: addMultiSelectItem(filters.naceCodes, item),
                  })
                }
                items={availableNaceCodes}
                placeholder={intl.formatMessage(messages.NaceCode)}
                onFilterBehaviourChanged={filterBehaviour => {
                  this.filterChanged({
                    filterBehaviours: {
                      naceCodes: filterBehaviour,
                    },
                  });
                }}
                filterBehaviour={filters.filterBehaviours?.naceCodes}
                withFilterBehaviour
              />
            </div>
          </div>
        );
      }
      case 'startDate': {
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={startDateFrom}
                startDateId="RelationOverviewstartDateFrom"
                endDate={startDateUntil}
                endDateId="RelationOverviewstartDateUntil"
                onDatesChange={({ startDate, endDate }) =>
                  this.filterChanged({
                    startDateFrom: startDate?.format('YYYY-MM-DD'),
                    startDateUntil: endDate?.format('YYYY-MM-DD'),
                  })
                }
                startDatePlaceholderText={intl.formatMessage(messages.StartDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.StartDateUntil)}
                block
              />
            </div>
          </div>
        );
      }

      case 'nationalityCode': {
        const { countries } = this.state;
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <select
                className="form-control"
                onChange={e =>
                  this.filterChanged({ nationalityCode: e.target.value ? e.target.value : null })
                }
                value={filters.nationalityCode || ''}
              >
                <option value="">{intl.formatMessage(messages.FilterNationality)}</option>
                {countries.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  dynamicFilterChanged = (item, selected, visibleItemIds) => {
    const { dynamicFilters } = this.state;
    const { filters } = this.props;

    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      if (visibleItemIds && !visibleItemIds.includes(x.id)) return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({
      dynamicFilters: newFilters,
    });

    if (
      !selected &&
      JSON.stringify(filters[item.id]) !== JSON.stringify(defaultRelationFilters[item.id])
    ) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultRelationFilters[item.id] });
    }
  };

  render() {
    const { filters, intl, countSelectedRelations, labels } = this.props;
    const { dynamicFilters } = this.state;

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultRelationFilters);

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);
    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];

    return (
      <div>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <TextSearch
                value={filters.searchValue}
                className="form-control"
                placeholder={intl.formatMessage(messages.SearchRelations)}
                onChange={value => this.filterChanged({ searchValue: value })}
              />
            </div>
          </div>

          <div className="col-lg-8 hidden-xs hidden-sm">
            <div className="row">
              <div className="col-md-6 hidden-xs hidden-sm">
                <div className="form-group">
                  <RelationTypeButtonGroup
                    selectedTypes={filters.type}
                    onChange={value => {
                      this.filterChanged({ type: value });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6 hidden-xs hidden-sm">
                <div className="form-group">
                  <CrmTypeButtonGroup
                    selectedTypes={filters.crmTypes}
                    onChange={value => {
                      this.filterChanged({ crmTypes: value });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                labels={labels}
                filters={filters}
                filterChanged={this.filterChanged}
                renderCustomFilter={this.renderDynamicFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="flex-row">
          <div className="btn-group save-filters">
            <FilterSave
              hasActiveFilters={hasFilters}
              screen="relations"
              filtersObject={filters}
              resetFilters={() => this.resetFilters()}
              setFilter={filter => this.setFilters(filter)}
            />
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon" />
                <FormattedMessage {...messages.MoreFilters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
          </div>
          <div className="margin10T" style={{ marginLeft: 'auto' }}>
            {countSelectedRelations}
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(RelationOverviewFilters);
